import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { State } from '@progress/kendo-data-query';
import { SessionSetting } from 'src/app/core/model/session-setting';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { CommunityProviderProgramSearchListService } from 'src/app/core/service/grid-data/grid-data-community-search.service';
import { MemberService } from 'src/app/core/service/member.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { HelperUtils } from 'src/app/core/utility/helper-utils';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { CommunityProviderSearchResult } from 'src/app/referral/model/community-provider-search-result';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import htmlToPdfmake from 'html-to-pdfmake';
import { SendEmailSMSContent } from 'src/app/shared/community-service-info/email-sms-content';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { UserService } from 'src/app/core/service/user.service';
import { ToastrService } from 'ngx-toastr';
import { DocumentService } from 'src/app/core/service/document.service';

@Component({
  standalone: false,
  selector: 'app-community-service-info',
  templateUrl: './community-service-info.component.html',
  styleUrls: ['./community-service-info.component.scss']
})
export class CommunityServiceInfoComponent implements OnInit
{
  @ViewChild('printBtn') public printBtn: ElementRef<HTMLElement>;
  @ViewChild('emailBodyContent') public emailBodyContent: any;
  @ViewChild('emailSubject') public emailSubject: any;
  @ViewChild('emailSMSBody') public emailSMSBody: any;

  @Input() public memberId: number;
  @Input() public programIds = [];
  @Input() public isPrint = false;
  @Input() public isEmail = false;
  @Input() public isSMS = false;
  @Input() public contactEmail = undefined;
  @Input() public contactSMS = undefined;

  @Output() public cancelling: EventEmitter<any> = new EventEmitter();

  public selectedServices = [];
  public distanceUnitDefault: string;
  public loading = true;
  public windowState = 'default';
  public patientLabel = {};
  public mapUrl: string;
  public showMap = false;
  public mileText: string;
  public kmText: string;
  public memberEmail = undefined;
  public memberSMS = undefined;
  public currentUser = this.authenticationService.currentUser;
  public emailSMSBodyToSend = '';
  public reportImageLoaded = false;
  public reportImageUrl: string;
  public hasReportImage = false;
  public reportDocumentId = null;
  public communityPortalEnabled = false;

  private memberAddress = "";

  constructor(
    private memberService: MemberService,
    private communityProviderProgramSearchList: CommunityProviderProgramSearchListService,
    private messageHandlerService: MessageHandlerService,
    private settingService: SettingService,
    private translate: TranslateService,
    private userService: UserService,
    private toastrService: ToastrService,
    private appConfigService: AppConfigService,
    private authenticationService: AuthenticationService,
    private documentService: DocumentService
  )
  {
    this.mapUrl = this.appConfigService.getConfig().google.mapBaseUrl + '?q=';
    this.showMap = this.appConfigService.getConfig().google.mapBaseUrl !== null;
  }

  public ngOnInit(): void
  {
    this.translate.get(['DISTANCE-UNIT-MILE', 'DISTANCE-UNIT-KM']).subscribe({
      next: (res) =>
      {
        this.mileText = res['DISTANCE-UNIT-MILE'];
        this.kmText = res['DISTANCE-UNIT-KM'];

        this.settingService.getCachedSessionSettings().subscribe({
          next: (sessionSetting: SessionSetting) =>
          {
            this.communityPortalEnabled = sessionSetting.communityPortalEnabled;
            this.distanceUnitDefault = sessionSetting.providerDistanceDisplay;
            this.patientLabel = { patientLabel: sessionSetting.patientLabel };
            this.loadPrograms();
          }
        });
      }
    });

    this.userService.getUserById(this.currentUser.id, false).subscribe({
      next: (user) =>
      {
        const userOrganizationName = user.organization ? user.organization.name : '';
        const userEmail = user ? user.email : '';
        this.emailSMSBodyToSend = this.emailSMSBody.nativeElement.innerHTML.replace('{{user}}',
          this.authenticationService.currentUser.name).replace('{{organization}}',
            userOrganizationName).replace('{{userEmail}}', userEmail);
      }
    });

    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting: SessionSetting) =>
      {
        this.reportDocumentId = sessionSetting.reportLogoDocumentId;
        this.getReportPhotoFromService();
      }
    });
  }

  public loadPrograms(): void
  {
    this.memberEmail = undefined;
    this.memberSMS = undefined;
    if (this.memberId !== undefined && this.memberId > 0)
    {
      this.memberService.getMemberMinimal(this.memberId).subscribe({
        next: (member) =>
        {
          // Set the patient address
          this.memberAddress = member.address !== null ? HelperUtils.formatAddress(member.address) : '';
          this.memberEmail = member.contact !== null && member.contact.email !== null &&
            member.contact.email !== '' ? member.contact.email : undefined;
          this.memberSMS = member.contact !== null && member.contact.cellPhoneNumber !== null &&
            member.contact.cellPhoneNumber !== '' ? member.contact.cellPhoneNumber : undefined;
          this.loadSelectedServices();
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    else
    {
      this.loadSelectedServices();
    }
  }

  public formatDistanceLabel(maxDistance: number): any
  {
    const unit = this.distanceUnitDefault === 'MILES' ? this.mileText : this.kmText;
    return `${unit}` + (maxDistance >= 1 ? 's' : '');
  }

  public closeProgramDialog(): void
  {
    this.cancelling.emit(true);
    this.loading = true;
  }

  public generateAndSendSMS(): void
  {
    this.isSMS = true;
    setTimeout(() =>
    {
      this.closeProgramDialog();    // Close as fast as possible

      const smsToUse = this.contactSMS !== undefined ? this.contactSMS : this.memberSMS;
      const smsContent: SendEmailSMSContent = {
        to: '',
        from: '',
        subject: this.emailSubject.nativeElement.innerHTML,
        body: this.emailSMSBodyToSend,
        attachment: null,
        phone: smsToUse,
        ids: this.programIds
      };

      // Send PDF to back-end and send sms
      this.userService.sendResourcesBySMS(smsContent).subscribe({
        next: () =>
        {
          this.translate.get('SMS-SENT-TO').subscribe({
            next: (res) =>
            {
              this.toastrService.success(res.replace('{{sms}}', smsToUse), null,
                {
                  enableHtml: true,
                  timeOut: 5000
                });
            }
          });
        },
        error: (errorResponse) =>
        {
          this.closeProgramDialog();
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    });
  }

  public generateAndSendEmail(): void
  {
    this.isEmail = true;
    setTimeout(() =>
    {
      (pdfMake as any).vfs = pdfFonts.vfs;

      const html = htmlToPdfmake(this.emailBodyContent.nativeElement.innerHTML, {
        tableAutoSize: true
      });

      this.closeProgramDialog();    // Close as fast as possible

      const documentDefinition = {
        content: html,
        pageBreakBefore: (currentNode) => currentNode.style && currentNode.style.indexOf('pdf-pagebreak-before') > -1
      };

      const pdfDocGenerator = pdfMake.createPdf(documentDefinition);
      const emailToUse = this.contactEmail !== undefined ? this.contactEmail : this.memberEmail;

      // Generate the PDF and build request
      pdfDocGenerator.getBase64((data: any) =>
      {
        const emailContent: SendEmailSMSContent = {
          to: emailToUse,
          from: this.currentUser.name,
          subject: this.emailSubject.nativeElement.innerHTML,
          body: this.emailSMSBodyToSend,
          attachment: data,
          phone: '',
          ids: []
        };

        // Send PDF to back-end and send email
        this.userService.sendResourcesByEmail(emailContent).subscribe({
          next: () =>
          {
            this.translate.get('EMAIL-SENT-TO').subscribe({
              next: (res) =>
              {
                this.toastrService.success(res.replace('{{email}}', emailToUse), null,
                  {
                    enableHtml: true,
                    timeOut: 5000
                  });
              }
            });
          },
          error: (errorResponse) =>
          {
            this.closeProgramDialog();
            this.messageHandlerService.displayError(errorResponse);
          }
        });
      });
    });
  }

  public getNotificationPreference(preference: string): string
  {
    return preference.replace("_", " ");
  }  

  private loadSelectedServices(): void
  {
    this.selectedServices = [];
    if (this.programIds !== undefined && this.programIds.length >= 0)
    {
      const state: State = {
        skip: 0,
        take: 99999,
        filter: {
          logic: 'and',
          filters: []
        },
        sort: []
      };

      // Set the services to read
      this.programIds.forEach(p =>
      {
        state.filter.filters.push({ field: 'id', operator: 'eq', value: p, logic: 'or' });
      });

      // Query the provider programs
      this.communityProviderProgramSearchList.queryByState(state, (value: CommunityProviderSearchResult) =>
      {
        value.data.forEach(p =>
        {
          this.communityProviderProgramSearchList.setCommunityServicesFields(p);
        });

        this.selectedServices = value.data;
        this.loading = false;

        // Print Services
        if (this.isPrint)
        {
          setTimeout(() =>
          {
            this.printBtn.nativeElement.click();
            this.closeProgramDialog();
          });
        }

        // Send Services by Email
        if (this.isEmail)
        {
          this.generateAndSendEmail();
        }

        // Send Services by SMS
        if (this.isSMS)
        {
          this.generateAndSendSMS();
        }
      }, `&postal=${this.memberAddress}`);
    }
    else
    {
      this.messageHandlerService.displayError('NO-COMMUNITY-PROVIDER');
    }
  }

  private getReportPhotoFromService()
  {
    this.reportImageLoaded = false;
    this.hasReportImage = false;
    if (this.reportDocumentId !== null && this.reportDocumentId > 0)
    {
      this.documentService.retrieveCachedDocument(`/documents/${this.reportDocumentId}/data`).subscribe({
        next: (data) =>
        {
          this.hasReportImage = data.content !== null;
          if (this.hasReportImage)
          {
            const base64 = data.content;
            this.reportImageUrl = 'data:image/jpeg;base64,' + base64;
          }
          this.reportImageLoaded = true;
        }
      });
    }
  }
}
